<template>
  <div v-if="wishlistStore.wishlist && wishlistStore.getWishlistLineItems?.length">
    <UiText v-if="isFromCartPage" size="lg" class="text-primary-600">{{ $t('wishlist.myWishlist') }}</UiText>
    <UiHeading :type="isFromCartPage ? 'h1' : 'h2'" class="my-4">
      {{ $t('wishlist.nbItems', { count: wishlistStore.getWishlistLineItems?.length }) }}
    </UiHeading>
    <div v-if="isMobile">
      <UiSlider id="wishlist-line-items">
        <div v-for="item in wishlistStore.getWishlistLineItems" :key="item.id" class="mt-6 w-44 shrink-0">
          <WishlistItem :item="item" />
        </div>
      </UiSlider>
    </div>
    <div v-for="item in wishlistStore.getWishlistLineItems" v-else :key="item.id" class="mt-10">
      <WishlistItem :item="item" />
      <UiDivider />
    </div>
    <div class="mt-3 flex flex-col justify-between md:flex-row">
      <UiButton
        id="wishlist-clear"
        variant="primary"
        type="neutral"
        class="mt-5"
        :icon="IconClose"
        icon-side="left"
        :disabled="isLoading"
        @click="isModalOpen = !isModalOpen"
      >
        {{ $t('wishlist.clearWishList') }}
      </UiButton>
      <UiButton
        id="wishlist-addAllToCart"
        class="mt-5"
        variant="primary"
        :icon="IconCart"
        icon-side="left"
        :loading="isLoading"
        @click="addAllToCart"
      >
        {{ $t('wishlist.addAllToCart') }}
      </UiButton>

      <UiModal
        id="wishlist-clear"
        :is-open="isModalOpen"
        :toggle="(value: boolean) => (isModalOpen = value)"
        :close="() => (isModalOpen = false)"
        :title="$t('removeModal.clearAllText')"
        :confirm-label="$t('general.confirm')"
        :cancel-label="$t('general.cancel')"
        :confirm-click="clearWishlist"
        :cancel-click="() => (isModalOpen = false)"
      >
        <UiText type="p">{{ $t('removeModal.content') }}</UiText>
      </UiModal>
    </div>
  </div>
  <div v-else class="w-full rounded-xl bg-white p-8 md:m-auto md:p-20">
    <UiHeading type="h2" class="text-center">{{ $t('wishlist.empty') }}</UiHeading>
    <UiButton id="wishlist-continue-shopping" variant="primary" class="mt-6 w-full" @click="goTo('/')">
      {{ $t('general.continueShopping') }}
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { IconCart, IconClose } from '#components';

import type { WishlistContentProps } from '~/components/Wishlist/types';
import type { WishlistItem } from '~/stores/wishlist/types';

import { useAlertStore } from '~/stores/alert/alertStore';
import { useAppStore } from '~/stores/app/appStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { CartActionType } from '~/stores/cart/types';
import { WishlistActionType } from '~/stores/wishlist/types';
import { useWishlistStore } from '~/stores/wishlist/wishlistStore';

const goTo = useGoTo();
const { isMobile } = useBreakpoints();
const { locale } = useI18n();

const alertStore = useAlertStore();
const wishlistStore = useWishlistStore();
const cartStore = useCartStore();
const appStore = useAppStore();

const isLoading = ref<boolean>(false);
const isModalOpen = ref<boolean>(false);

const clearWishlist = (showMessage = true): void => {
  isLoading.value = true;

  if (!wishlistStore.wishlist) {
    isLoading.value = false;
    return;
  }

  wishlistStore
    .updateWishlist({
      actions: wishlistStore.wishlist.items.map((item: WishlistItem) => {
        return {
          id: item.id,
          type: WishlistActionType.REMOVE_ITEM,
        };
      }),
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(
      () =>
        showMessage &&
        alertStore.add({
          text: 'wishlist.wishlistClearedSuccessMessage',
          title: 'general.successTitle',
          type: 'positive',
        }),
    )
    .finally(() => (isLoading.value = false));
};

const addAllToCart = (): void => {
  isLoading.value = true;

  if (!wishlistStore.wishlist) {
    isLoading.value = false;
    return;
  }

  cartStore
    .updateCart({
      actions: wishlistStore.wishlist.items.map((item: WishlistItem) => {
        return {
          quantity: 1,
          sku: item.sku,
          type: CartActionType.ADD_ITEM,
        };
      }),
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() => {
      alertStore.add({
        text: 'wishlist.wishlistAddedToCartSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      });
      clearWishlist(false);
    })
    .finally(() => (isLoading.value = false));
};

defineProps<WishlistContentProps>();
</script>
